import { useState } from "react";
import Swal from "sweetalert2";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/OptionChain";

function UpdateFyersToken() {
  const [authCode, setAuthCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showCode, setShowCode] = useState(false);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!e.target.checkValidity()) {
      e.target.classList.add("was-validated");
      return;
    }

    const payload = { authCode: authCode.trim() };
    console.log("📤 UpdateAuthCode payload:", payload);
    setSubmitting(true);

    try {
      const res = await fetch(`${BASE_URL}/update-auth-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      console.log("✅ UpdateAuthCode response:", data);
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      await Swal.fire({
        title: "Token Updated!",
        text: data?.message ?? "Fyers auth code has been updated successfully.",
        icon: "success",
        confirmButtonColor: "#198754",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });

      setAuthCode("");
      e.target.classList.remove("was-validated");
    } catch (err) {
      console.error("❌ UpdateAuthCode error:", err);
      await Swal.fire({
        title: "Update Failed",
        text: err.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pt-1 pb-3 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div>
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    Update Fyers Token
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    Update the Fyers Auth Code for Option Chain data feed
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-8 col-12">
              <form
                className="needs-validation"
                noValidate
                onSubmit={handleSubmit}
              >
                {/* ── Card ── */}
                <div className="border rounded-4 p-4 bg-light">
                  {/* Info banner */}
                  <div className="alert alert-info rounded-3 d-flex align-items-center gap-2 py-2 mb-3">
                    <i className="bi bi-info-circle-fill text-info mt-1 flex-shrink-0"></i>
                    <div className="small">
                      Generate your auth code from the{" "}
                      <a
                        href="https://api-t1.fyers.in/api/v3/generate-authcode?client_id=47PI9TADW7-100&redirect_uri=https%3A%2F%2Ftrade.fyers.in%2Fapi-login%2Fredirect-uri%2Findex.html&response_type=code&state=sample_state&nonce=sample_nonce"
                        target="_blank"
                        rel="noreferrer"
                        className="fw-semibold"
                      >
                        Fyers API Dashboard
                      </a>{" "}
                      and paste it below. The token is used for live Option
                      Chain data.
                    </div>
                  </div>

                  {/* Auth Code field */}
                  <div className="mb-3">
                    <label className="form-label small" htmlFor="authCode">
                      Auth Code <span className="text-danger fw-bold">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type={showCode ? "text" : "password"}
                        id="authCode"
                        className="form-control form-control-sm"
                        placeholder="Paste your Fyers auth code here…"
                        value={authCode}
                        onChange={(e) => setAuthCode(e.target.value)}
                        required
                        style={{
                          fontFamily: "monospace",
                          letterSpacing: "0.03em",
                        }}
                        autoComplete="off"
                        spellCheck={false}
                      />
                      {/* Show / hide toggle */}
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setShowCode((p) => !p)}
                        title={showCode ? "Hide" : "Show"}
                      >
                        <i
                          className={`bi ${showCode ? "bi-eye-slash" : "bi-eye"}`}
                        ></i>
                      </button>
                      {/* Copy button */}
                      {authCode && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          title="Copy"
                          onClick={() => {
                            navigator.clipboard.writeText(authCode);
                            Swal.fire({
                              toast: true,
                              position: "top-end",
                              icon: "success",
                              title: "Copied!",
                              showConfirmButton: false,
                              timer: 1500,
                              customClass: { popup: "rounded-3" },
                            });
                          }}
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      )}
                    </div>
                    <div className="invalid-feedback">
                      Please enter the auth code.
                    </div>
                    {authCode && (
                      <div className="form-text text-secondary mt-1">
                        <i className="bi bi-shield-lock me-1"></i>
                        {authCode.length} characters
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <button
                    className="btn btn-success fw-normal px-5 py-1 fs-6 d-inline-flex align-items-center gap-2"
                    type="submit"
                    disabled={submitting || !authCode.trim()}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                        Updating…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-repeat"></i>
                        Update Token
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default UpdateFyersToken;
